import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { C, FONT_MONO } from "@/theme";
import type { MediaKind, PeerStats } from "@/media/types";
import { PlayIcon } from "@/components/icons";

export type VideoTileProps = {
  stream: MediaStream | null;
  label: string;
  kind: MediaKind;
  stats?: PeerStats;
  live?: boolean;
  muted?: boolean;
  mirrored?: boolean;
  focused?: boolean;
  onFocus?: () => void;
  /** Selo desenhado dentro da pilula do rotulo: microfone cortado, silenciado. */
  labelBadge?: ReactNode;
  /** Camada livre sobre o tile — usada pelo controle de volume no hover. */
  overlay?: ReactNode;
  /** Troca a cor da borda quando o tile precisa de destaque (silenciado pela moderacao). */
  borderColor?: string;
  /** Substitui o aviso padrao "AGUARDANDO VÍDEO" quando `stream` e nulo. */
  emptyState?: ReactNode;
};

type TrackInfo = { width: number; height: number; frameRate: number };

/** Fundo listrado do desenho, usado enquanto a trilha nao chega. */
const STRIPES = `repeating-linear-gradient(45deg,${C.hoverStrong} 0 12px,#1d1d1d 12px 24px)`;

function formatBitrate(kbps: number): string {
  return kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mb/s` : `${Math.round(kbps)} kb/s`;
}

function codecLabel(codec: string): string {
  return codec.replace(/^(video|audio)\//i, "").toUpperCase();
}

export function VideoTile({
  stream,
  label,
  kind,
  stats,
  live,
  muted,
  mirrored,
  focused,
  onFocus,
  labelBadge,
  overlay,
  borderColor,
  emptyState,
}: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [track, setTrack] = useState<TrackInfo | null>(null);

  /** O navegador pode recusar o play automatico; nesse caso mostramos o botao. */
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    if (element.srcObject !== stream) element.srcObject = stream;
    if (!stream) {
      setBlocked(false);
      return;
    }
    let alive = true;
    element.play().then(
      () => {
        if (alive) setBlocked(false);
      },
      () => {
        if (alive) setBlocked(true);
      },
    );
    return () => {
      alive = false;
    };
  }, [muted, stream]);

  /** Resolucao e taxa reais da trilha, para quando ainda nao ha estatisticas. */
  useEffect(() => {
    if (!stream) {
      setTrack(null);
      return;
    }
    const read = () => {
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        setTrack(null);
        return;
      }
      const settings = videoTrack.getSettings();
      const next: TrackInfo = {
        width: settings.width ?? 0,
        height: settings.height ?? 0,
        frameRate: Math.round(settings.frameRate ?? 0),
      };
      setTrack(prev =>
        prev &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.frameRate === next.frameRate
          ? prev
          : next,
      );
    };
    read();
    const timer = window.setInterval(read, 2_000);
    return () => window.clearInterval(timer);
  }, [stream]);

  const handlePlay = useCallback((event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const element = videoRef.current;
    if (!element) return;
    element.play().then(
      () => setBlocked(false),
      () => setBlocked(true),
    );
  }, []);

  const handleKey = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!onFocus) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onFocus();
    },
    [onFocus],
  );

  const isScreen = kind === "screen";
  const edge = borderColor ?? (isScreen ? "var(--ac,#1F4FD8)" : C.lineTile);
  const width = stats?.width || track?.width || 0;
  const height = stats?.height || track?.height || 0;
  const fps = Math.round(stats?.frameRate || track?.frameRate || 0);
  const info: string[] = [];
  if (width > 0 && height > 0) info.push(`${width}×${height}`);
  if (fps > 0) info.push(`${fps} fps`);
  if (stats && stats.bitrateKbps > 0) info.push(formatBitrate(stats.bitrateKbps));
  if (stats && stats.codec) info.push(codecLabel(stats.codec));
  const showInfo = info.length > 0 && (isScreen || Boolean(stats));

  return (
    <div
      onClick={onFocus}
      onKeyDown={handleKey}
      role={onFocus ? "button" : undefined}
      tabIndex={onFocus ? 0 : undefined}
      aria-label={onFocus ? `Abrir ${label} em tela cheia` : undefined}
      style={{
        position: "relative",
        width: "100%",
        height: focused ? "100%" : undefined,
        aspectRatio: focused ? undefined : isScreen ? "16/7" : "16/10",
        borderRadius: focused ? 0 : 14,
        overflow: "hidden",
        border: focused ? "0" : `1px solid ${edge}`,
        background: stream ? (focused ? "#0d0d0d" : C.ink) : STRIPES,
        display: "grid",
        placeItems: "center",
        cursor: onFocus ? "pointer" : "default",
        outline: "none",
        animation: focused ? undefined : "pop 220ms ease",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted ?? true}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          objectFit: "contain",
          background: "transparent",
          transform: mirrored ? "scaleX(-1)" : undefined,
        }}
      />

      {!stream &&
        (emptyState ?? (
          <span
            style={{
              position: "absolute",
              fontFamily: FONT_MONO,
              fontSize: 11,
              letterSpacing: ".12em",
              color: C.textFaint,
            }}
          >
            AGUARDANDO VÍDEO
          </span>
        ))}

      {live && (
        <span
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            fontFamily: FONT_MONO,
            fontSize: 9.5,
            fontWeight: 600,
            letterSpacing: ".1em",
            padding: "3px 8px",
            borderRadius: 6,
            background: "var(--ac,#1F4FD8)",
            color: C.ink,
          }}
        >
          AO VIVO
        </span>
      )}

      {showInfo && (
        <span
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            fontFamily: FONT_MONO,
            fontSize: 9.5,
            letterSpacing: ".06em",
            padding: "3px 8px",
            borderRadius: 6,
            background: "rgba(0,0,0,.5)",
            color: C.textSoft,
          }}
        >
          {info.join(" · ")}
        </span>
      )}

      <span
        style={{
          position: "absolute",
          left: 10,
          bottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 9px",
          borderRadius: 7,
          background: "rgba(0,0,0,.5)",
          fontFamily: FONT_MONO,
          fontSize: 10.5,
          color: C.textStrong,
        }}
      >
        {label}
        {labelBadge}
      </span>

      {overlay}

      {blocked && (
        <button
          onClick={handlePlay}
          title="O navegador bloqueou a reprodução automática"
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            border: 0,
            cursor: "pointer",
            background: "rgba(0,0,0,.55)",
          }}
        >
          <span
            className="hov-bright"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              borderRadius: 999,
              border: `1px solid ${C.lineStrong}`,
              background: C.raised,
              fontFamily: FONT_MONO,
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: ".1em",
              color: C.textStrong,
            }}
          >
            <PlayIcon size={12} color={C.accent} />
            TOCAR
          </span>
        </button>
      )}
    </div>
  );
}
